import React, { useState, useEffect } from 'react';
import TimingLayout from '../../components/TimingLayout';
import { Row, Col, Button, Form, Dropdown, ButtonGroup } from 'react-bootstrap';
import { MdDateRange } from 'react-icons/md';
import { FaClock, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa';
import axios from 'axios';
import ReasonModal from './ReasonModal';
import './time-history.css';

const rowsPerPage = 10;

const TimeHistory = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [reasonType, setReasonType] = useState('');

  // Function to format time
  const formatTime = (datetime) => {
    if (!datetime) return '-';
    const date = new Date(datetime);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Function to format date
  const formatDate = (date) => {
    if (!date) return '-';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(date).toLocaleDateString('en-US', options);
  };

  // Function to determine row class based on late minutes or early close minutes
  const getRowClass = (item) => {
    if ((item.lateMinutes && item.lateMinutes > 0) ||
        (item.earlyCloseMinutes && item.earlyCloseMinutes > 0)) {
      return 'late-row';
    }
    return 'on-time-row';
  };

  useEffect(() => {
    setLoading(true);
    setError(null);

    const userId = localStorage.getItem('userId');

    if (!userId) {
      setLoading(false);
      setError('User is not logged in or userId is missing.');
      return;
    }

    // Fetch attendance records
    axios
      .get('https://backend.ems.hiradentallab.com/api/attendance/current-month', {
        params: { userId },
      })
      .then((response) => {
        setData(response.data.data);
        setFilteredData(response.data.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching attendance:', error);
        setError(error.response?.data?.message || 'An error occurred.');
        setLoading(false);
      });
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (!query) {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item) =>
        item.date && item.date.toLowerCase().includes(query)
      );
      setFilteredData(filtered);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= Math.ceil(filteredData.length / rowsPerPage)) {
      setCurrentPage(pageNumber);
    }
  };

  const pageRange = () => {
    const pages = [];
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleReasonClick = (record, type) => {
    setCurrentRecord(record);
    setReasonType(type);
    setShowReasonModal(true);
  };

  const handleSubmitReason = async (reason) => {
    try {
      const userId = localStorage.getItem('userId');
      const userName = localStorage.getItem('userName');
      const department = localStorage.getItem('department');

      if (!userId) {
        alert('Please log in to submit a reason');
        return;
      }

      const response = await axios.post(
        `https://backend.ems.hiradentallab.com/api/attendance/${currentRecord._id}/reason`,
        {
          type: reasonType,
          reason,
          userId,
          userName,
          department
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // Update local state
        const updatedData = data.map(item => {
          if (item._id === currentRecord._id) {
            return {
              ...item,
              [`${reasonType}Reason`]: reason,
              [`${reasonType}Approved`]: false // Set to pending approval
            };
          }
          return item;
        });

        setData(updatedData);
        setFilteredData(updatedData);
        setShowReasonModal(false);
        alert('Reason submitted successfully!');
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error submitting reason:', error);
      alert(error.response?.data?.message || error.message || 'Failed to submit reason');
    }
  };

  const renderReasonButton = (record, type) => {
    const reasonField = `${type}Reason`;
    const approvedField = `${type}Approved`;
    const minutesField = type === 'late' ? 'lateMinutes' :
                        type === 'earlyClose' ? 'earlyCloseMinutes' :
                        'extraMinutes';

    if (record[minutesField] && record[minutesField] > 0) {
      if (!record[reasonField] || record[reasonField] === '') {
        // No reason submitted
        return (
          <Button
            variant="link"
            size="sm"
            onClick={() => handleReasonClick(record, type)}
            className="reason-button"
            title="Add Reason"
          >
            <FaClock className="reason-icon" />
          </Button>
        );
      } else if (record[reasonField] && !record[approvedField]) {
        // Reason submitted, pending approval
        return (
          <Button
            variant="link"
            size="sm"
            onClick={() => handleReasonClick(record, type)}
            className="reason-button"
            title="View Pending Reason"
          >
            <FaHourglassHalf className="reason-icon pending" />
          </Button>
        );
      } else if (record[reasonField] && record[approvedField]) {
        // Reason approved
        return (
          <Button
            variant="link"
            size="sm"
            onClick={() => handleReasonClick(record, type)}
            className="reason-button"
            title="View Approved Reason"
          >
            <FaCheckCircle className="reason-icon approved" />
          </Button>
        );
      }
    }
    return null;
  };

  return (
    <div className='container-fluid'>
      <TimingLayout>
        <h1 className="current-month-title">
          <MdDateRange style={{ marginRight: '10px' }} /> Current Month
        </h1>
        <hr />
        <div className="container" style={{ padding: '20px', backgroundColor: '#bbdefb', maxWidth: '1800px', width: '100%' }}>
          <div className="filter-container">
            <Col xs={12} md={6} className="filter-left">
              <Dropdown as={ButtonGroup}>
                <Button variant="primary">Show Entries</Button>
                <Dropdown.Toggle split variant="primary" id="dropdown-split-basic" />
                <Dropdown.Menu>
                  <Dropdown.Item>10</Dropdown.Item>
                  <Dropdown.Item>25</Dropdown.Item>
                  <Dropdown.Item>50</Dropdown.Item>
                  <Dropdown.Item>100</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
            <Col xs={12} md={6} className="filter-right">
              <Form className="d-flex justify-content-end" onSubmit={(e) => e.preventDefault()}>
                <Form.Control
                  type="search"
                  placeholder="Search by date (e.g., 2024-12-25)"
                  aria-label="Search"
                  className="search-bar"
                  value={searchQuery}
                  onChange={handleSearch}
                  style={{ maxWidth: '300px' }}
                />
              </Form>
            </Col>
          </div>
          <div className="table-responsive">
            <table
              className="table table-striped time-history-table"
              style={{
                backgroundColor: '#bbdefb',
                border: '2px solid #0353a4',
                borderRadius: '8px',
                marginTop: '20px',
                maxWidth: '1800px',
                width: '100%',
              }}
            >
              <thead>
                <tr className="time-history-table-header">
                  <th>SR</th>
                  <th>Date</th>
                  <th>CheckIn</th>
                  <th>CheckOut</th>
                  <th>Worked Hours</th>
                  <th>Late Minutes</th>
                  <th>Early Close Minutes</th>
                  <th>OverTime</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center' }}>
                      Loading...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', color: 'red' }}>
                      {error}
                    </td>
                  </tr>
                ) : filteredData.length ? (
                  filteredData
                    .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                    .map((item, index) => (
                      <tr key={index} className={getRowClass(item)}>
                        <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                        <td>{formatDate(item.date)}</td>
                        <td>{formatTime(item.checkIn)}</td>
                        <td>{formatTime(item.checkOut)}</td>
                        <td>{item.workedHours || '-'}</td>
                        <td>
                          {item.lateMinutes || '-'}
                          {renderReasonButton(item, 'late')}
                        </td>
                        <td>
                          {item.earlyCloseMinutes || '-'}
                          {renderReasonButton(item, 'earlyClose')}
                        </td>
                        <td>
                          {item.extraMinutes || '-'}
                          {renderReasonButton(item, 'overtime')}
                        </td>
                        <td>
                          {/* Additional actions can be added here */}
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center' }}>
                      No data available.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="timing-history-table-footer">
                <tr className="table-header-footer">
                  <td>Total</td>
                  <td>{filteredData.length}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
            <div className="pagination-container">
              <Row className="custom-row">
                <Col xs={12} md={9} className="custom-col-left">
                  Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, filteredData.length)} of{' '}
                  {filteredData.length} entries
                </Col>
                <Col xs={12} md={3} className="custom-col-right">
                  <nav>
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <a
                          href="/#"
                          className="page-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage - 1);
                          }}
                        >
                          Previous
                        </a>
                      </li>
                      {pageRange().map((pageNumber) => (
                        <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                          <a
                            href="/#"
                            className="page-link"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(pageNumber);
                            }}
                          >
                            {pageNumber}
                          </a>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === Math.ceil(filteredData.length / rowsPerPage) ? 'disabled' : ''}`}>
                        <a
                          href="/#"
                          className="page-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage + 1);
                          }}
                        >
                          Next
                        </a>
                      </li>
                    </ul>
                  </nav>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </TimingLayout>

      <ReasonModal
        show={showReasonModal}
        onHide={() => setShowReasonModal(false)}
        onSubmit={handleSubmitReason}
        type={reasonType}
        record={currentRecord}
      />
    </div>
  );
};

export default TimeHistory;